import { Row, Popover, Tag } from 'antd';
import { ThreeDotsVertical, Pencil, Trash, Eye, ArrowCounterclockwise } from 'react-bootstrap-icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import TableComponent from '../common/TableComponent';
import { ActionMenu } from '../common/ActionMenu';
import { CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { PermissionEnum } from '../../enums/PermissionEnum';
import { usePermissions } from '../../hooks/usePermissions';
import { getProductUnitDisplayNameWithTranslation } from '../../enums/ProductUnitEnum';
import { useIsOnlineContext } from '../../contexts/IsOnlineContext';
import { ActiveFilterEnum } from '../../enums/ActiveFilterEnum';

interface Product {
  id: number;
  name: string;
  mainProductName?: string;
  presentation?: string;
  brand?: string;
  refId: string;
  basketIds?: number[];
  productCategoryId?: number;
  createTime: string;
  updateTime: string;
  synced?: number;
  equivalentAmount?: number;
  priceCount?: number;
  avgUnitPrice?: number;
  isActive?: boolean;
}

interface ProductCategory {
  id: number;
  mainCategory: string;
  category: string;
  subCategory?: string;
}

interface ProductListFormProps {
  products: Product[];
  handleEdit?: (id: string) => void;
  handleDelete?: (id: string) => void;
  getDeleteConfirmation?: (id: string) => void;
  getRestoreConfirmation?: (id: string, numericId: number) => void;
  getPresentationDetails?: (id: number, name: string) => void;
  totalProducts: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number, size?: number) => void;
  loading?: boolean;
  isPolling?: boolean;
  pollingMessage?: string;
  statusFilter?: ActiveFilterEnum;
}

type ProductMenuItem = {
  text: string;
  icon: React.ReactNode;
  click: () => void;
  disabled: boolean;
  description?: string;
};

const ProductList: React.FC<ProductListFormProps> = ({
  products,
  handleEdit,
  getDeleteConfirmation,
  getRestoreConfirmation,
  getPresentationDetails,
  totalProducts,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  loading = false,
  isPolling = false,
  pollingMessage = 'Checking for new product...',
  statusFilter = 'active',
}) => {
  const { t } = useTranslation(['product', 'form']);
  const { hasPermission } = usePermissions();
  const { isOnline } = useIsOnlineContext();
  const canViewPresentations = hasPermission(PermissionEnum.VIEW_PRESENTATION);
  const canUpdatePresentations = hasPermission(PermissionEnum.UPDATE_PRESENTATION);
  const canDeletePresentations = hasPermission(PermissionEnum.DELETE_PRESENTATION);
  const canShowPresentationActions = canViewPresentations || canUpdatePresentations || canDeletePresentations;

  const getProductColumns = () => [
    {
      title: t('productLinked'),
      dataIndex: 'mainProductName',
      key: 'mainProductName',
      width: 150,
      render: (mainProductName: string, record: Product) => (
        <span className="text-gray-600">{mainProductName || record.name || 'N/A'}</span>
      ),
    },
    {
      title: t('presentation'),
      dataIndex: 'presentation',
      key: 'presentation',
      width: 120,
      render: (presentation: string) => (
        <span className="text-gray-600">{presentation || 'N/A'}</span>
      ),
    },
    {
      title: t('brand'),
      dataIndex: 'brand',
      key: 'brand',
      width: 120,
      render: (brand: string) => <span className="text-gray-600">{brand || 'N/A'}</span>,
    },
    {
      title: t('name'),
      key: 'fullName',
      width: 200,
      render: (_: unknown, record: Product) => (
        <span className="text-gray-600">
          {`${record.mainProductName || record.name || ''} ${record.presentation || ''} ${record.brand || ''}`.trim() ||
            'N/A'}
        </span>
      ),
    },
    {
      title: t('unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 80,
      align: 'center' as const,
      render: (unit: string) => (
        <span className="text-gray-600">
          {unit ? getProductUnitDisplayNameWithTranslation(unit, t) : 'N/A'}
        </span>
      ),
    },
    {
      title: t('category'),
      dataIndex: 'productCategory',
      key: 'category',
      width: 150,
      align: 'center' as const,
      render: (category: ProductCategory | null) => (
        <span className="text-gray-600">
          {category && category.category
            ? `${category.mainCategory} > ${category.category}`
            : category && category.mainCategory
              ? `${category.mainCategory}`
              : 'N/A'}
        </span>
      ),
    },
    {
      title: t('equivalentAmount'),
      dataIndex: 'equivalentAmount',
      key: 'equivalentAmount',
      width: 100,
      align: 'center' as const,
      render: (amount: number) => (
        <span className="text-gray-600">
          {amount !== null && amount !== undefined ? amount : 'N/A'}
        </span>
      ),
    },
    {
      title: t('pricePerPresentation'),
      dataIndex: 'avgUnitPrice',
      key: 'avgUnitPrice',
      width: 150,
      align: 'center' as const,
      render: (avgUnitPrice: number | undefined | null) => {
        if (avgUnitPrice === undefined || avgUnitPrice === null || avgUnitPrice === 0) {
          return <span className="text-gray-600">-</span>;
        }
        return (
          <span className="text-gray-600">
            {avgUnitPrice.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      },
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'status',
      width: 100,
      render: (isActive: boolean) => (
        <Tag
          color={isActive ? 'green' : 'red'}
          className="px-3 py-0.5 text-xs font-medium"
        >
          {isActive ? t('statusActive') : t('statusInactive')}
        </Tag>
      ),
    },
    {
      title: t('created'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 120,
      align: 'center' as const,
      render: (createTime: string) => (
        <span className="text-sm text-gray-600">
          {dayjs(Number(createTime)).format('YYYY-MM-DD')}
        </span>
      ),
    },
    {
      title: t('synced'),
      dataIndex: 'synced',
      key: 'synced',
      width: 20,
      align: 'center' as const,
      render: (_: unknown, record: Product) => (
        <span className="text-gray-600">
          {record?.synced === 0 ? (
            <SyncOutlined title={t('notSynced')} />
          ) : (
            <CheckCircleOutlined className="text-tag-green" title={t('synced')} />
          )}
        </span>
      ),
    },
    ...(canShowPresentationActions
      ? [
        {
          title: t('action'),
          dataIndex: 'action',
          key: 'action',
          width: 100,
          align: 'center' as const,
          render: (_: unknown, record: Product) => {
            const isActionDisabled = record.synced == 0 || !record?.id;
            const disabledDescription = isActionDisabled ? t('notSynced') : undefined;
            const menuItems: ProductMenuItem[] = [];

            if (statusFilter === 'inactive' || record?.isActive === false) {
              if (canUpdatePresentations) {
                menuItems.push({
                  text: t('restore'),
                  icon: <ArrowCounterclockwise size={16} className="text-primary-blue-icon" />,
                  click: () => getRestoreConfirmation?.(record.refId, record.id),
                  disabled: !isOnline,
                  description: !isOnline ? t('offlineRestoreInfoTitle') : undefined,
                });
              }
            } else {
              if (canUpdatePresentations) {
                menuItems.push({
                  text: t('edit'),
                  icon: <Pencil size={16} className="text-primary-blue-icon" />,
                  click: () => handleEdit?.(record.refId),
                  disabled: false,
                });
              }

              if (canDeletePresentations) {
                menuItems.push({
                  text: t('delete'),
                  icon: <Trash size={16} className="text-primary-blue-icon" />,
                  click: () => getDeleteConfirmation?.(record.refId, record.id),
                  disabled: !isOnline,
                  description: !isOnline ? t('offlineDeleteInfoTitle') : undefined,
                });
              }
            }

            if (menuItems.length === 0) {
              return null;
            }

            return (
              <Popover
                content={<ActionMenu menuItems={menuItems} />}
                className="cursor-pointer"
                placement="bottom"
                trigger="hover"
                overlayStyle={{ zIndex: 800 }}
              >
                <Row justify="center">
                  <ThreeDotsVertical size={15} />
                </Row>
              </Popover>
            );
          },
        },
      ]
      : []),
  ];

  return (
    <>
      {isPolling && (
        <div className="mb-4 flex items-center justify-center rounded-md bg-blue-50 p-3 text-blue-700">
          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
          <span className="text-sm font-medium">{pollingMessage}</span>
        </div>
      )}
      {/* <BasicCard> */}
      {/* <div className="p-4"> */}
      <div className="mt-6 w-full overflow-x-auto">
        <TableComponent
          tableData={products}
          pageSize={pageSize}
          currentPage={currentPage}
          totalItems={totalProducts}
          onPaginationChanged={onPageChange}
          columns={getProductColumns()}
          loading={loading}
          showHeader
        />
      </div>
      {/* </div> */}
      {/* </BasicCard> */}
    </>
  );
};

export default ProductList;
